import * as React from 'react';

import { translate } from '~l10n/client';
import { Draft } from 'immer';
import { useImmerReducer } from 'use-immer';

import { FinishRoute } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { commonLinkStyle } from '~shared/components/Link';
import { useFinishActivity } from '~teacher/hook';

import { assertUnreachable } from '../../../util/assertUnreachable';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  activityInstanceId: number;
  openLaunchQuizDialog: () => void;
}

export function ConfirmStopRunningActivity({
  isOpen,
  onClose,
  activityInstanceId,
  openLaunchQuizDialog,
}: Props) {
  const finishActivityMutation = useFinishActivity();

  const handleLaunchNewActivity = () => {
    finishActivityMutation.mutate(
      { activityInstanceId, finishRoute: FinishRoute.StartNewActivity },
      { onError: (err) => console.error(err) }
    );
    openLaunchQuizDialog();
  };

  const onNoClick = () => onClose();

  // Since the link is dynamically generated, we need to use a ref callback to
  // assign our common link styles to the link.
  const refCallback = (node: HTMLSpanElement) => {
    if (!node) return;
    const link = node.querySelector('a');
    if (!link) return;

    link.className = `${commonLinkStyle} font-500 text-cyan-700 hocus:text-cyan-600`;
  };

  return (
    <DialogConfirmation
      closeLabel={translate('Close')}
      actions={[
        <UIButton key="cancel" onClick={onNoClick}>
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          appearance="primary"
          key="launch-new"
          onClick={handleLaunchNewActivity}
        >
          {translate('Finish Activity')}
        </UIButton>,
      ]}
      open={isOpen}
      onClose={onClose}
      id="confirm-launch-quiz-dialog"
      title={translate(
        'Launching a New Activity Will End the Current Activity'
      )}
    >
      <span
        ref={refCallback}
        dangerouslySetInnerHTML={{
          __html: translate(
            'You can <a href=\"#rooms\">add another room</a> to keep your current activity running.' /* eslint-disable-line */ /* prettier-ignore */
          ),
        }}
      />
    </DialogConfirmation>
  );
}

interface ConfirmStopRunningActivityState {
  isOpen: boolean;
  onClose: () => void;
  openLaunchQuizDialog: () => void;
}

type ConfirmStopRunningActivityAction =
  | { type: 'close' }
  | {
      type: 'open';
      payload: { onClose: () => void; openLaunchQuizDialog: () => void };
    };

function confirmStopRunningActivityReducer(
  draft: Draft<ConfirmStopRunningActivityState>,
  action: ConfirmStopRunningActivityAction
) {
  switch (action.type) {
    case 'open': {
      const { onClose, openLaunchQuizDialog } = action.payload;
      draft.isOpen = true;
      draft.onClose = onClose;
      draft.openLaunchQuizDialog = openLaunchQuizDialog;
      return draft;
    }
    case 'close': {
      draft.isOpen = false;
      draft.onClose = null;
      draft.openLaunchQuizDialog = null;
      return draft;
    }
    default:
      assertUnreachable(action);
  }
}

export type DispatchConfirmStopRunningActivityAction =
  React.Dispatch<ConfirmStopRunningActivityAction>;

export function useConfirmStopRunningActivity(): [
  ConfirmStopRunningActivityState,
  DispatchConfirmStopRunningActivityAction
] {
  return useImmerReducer(confirmStopRunningActivityReducer, {
    isOpen: false,
    openLaunchQuizDialog: null,
    onClose: null,
  });
}
